import { SearchIcon, ArrowDown } from "lucide-react";
import styles from "./Invoice.module.scss";
import classNames from "classnames/bind";
import { useEffect, useMemo, useRef, useState } from "react";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { handleGetInvoiceJobDetailsList } from "~/thunks/job-details/jobDetailsThunk";
import { downloadBlob, notify } from "~/utils/helper";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";
import dayjs from "dayjs";
import { ActionIcon, ArrowDownIcon } from "~/assets/icons";
import { IInvoiceJobDetails, InvoiceDetailsAction, InvoiceFormSource } from "~/utils/interface/jobDetails";
import jobDetailsApi from "~/apis/jobDetails";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import RequestPaymentModal from "../request-payment-modal/RequestPaymentModal";
import ModalAlertDelete from "~/components/common/modal-alert-delete/ModalAlertDelete";

const cx = classNames.bind(styles);

interface InvoiceProps {
  jobId: string;
  handleCreateInvoice: (listInvoice: IInvoiceJobDetails[], source?: InvoiceFormSource) => void;
  getListInvoice: boolean;
  setGetListInvoice: (value: boolean) => void;
  handleViewDetails: (invoiceId: string, action?: InvoiceDetailsAction) => void;
}

// Every invoice status (JobInvoiceStatusEnum on BE) — used by the row status menu and the list filter.
const STATUS_OPTIONS = ["Draft", "Sent", "Open", "Closed", "Cancelled", "Void"].map((status) => ({
  label: status,
  value: status,
}));

const FILTER_STATUS_OPTIONS = [{ label: "All Statuses", value: "" }, ...STATUS_OPTIONS];

const Invoice = ({
  jobId,
  handleCreateInvoice,
  getListInvoice,
  setGetListInvoice,
  handleViewDetails,
}: InvoiceProps) => {
  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { invoiceJobDetailsList } = useAppSelector((state) => state.jobDetails);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [activeStatusMenu, setActiveStatusMenu] = useState<string | null>(null);
  const [statusMenuPosition, setStatusMenuPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    showAbove?: boolean;
  }>({
    top: 0,
    left: 0,
  });
  const [statusUpdating, setStatusUpdating] = useState<Record<string, boolean>>({});
  const [optimisticStatus, setOptimisticStatus] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showItemsDropdown, setShowItemsDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemsPerPageOptions = [10, 20, 50, 100];

  const [formData, setFormData] = useState({
    status: "",
  });
  const [isRequestPaymentOpen, setIsRequestPaymentOpen] = useState(false);
  const [deleteInvoiceId, setDeleteInvoiceId] = useState<string | null>(null);

  const handleDeleteInvoice = async () => {
    if (!deleteInvoiceId) return;
    try {
      setIsLoading(true);
      await jobDetailsApi.deleteInvoice(jobId, deleteInvoiceId);
      notify("success", "Invoice deleted successfully");
      setGetListInvoice(true);
    } catch (error: any) {
      notify("error", error?.response?.data?.message || "Failed to delete invoice");
    } finally {
      setDeleteInvoiceId(null);
      setIsLoading(false);
    }
  };


  const handleFieldChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

  };

  useEffect(() => {
    if (jobId || getListInvoice === true) {
      setIsLoading(true);
      dispatch(
        handleGetInvoiceJobDetailsList({
          id: jobId,
          params: {},
        })
      )
        .unwrap()
        .then((res) => {
          setIsLoading(false);
          setGetListInvoice(false);
        })
        .catch((error: any) => {
          notify(
            "error",
            `Failed to fetch invoice list: ${error.response.data.message}`
          );
          setIsLoading(false);
          setGetListInvoice(false);
        });
    }
  }, [dispatch, jobId, getListInvoice, setGetListInvoice]);

  const filteredInvoices = useMemo(() => {
    let filtered = invoiceJobDetailsList?.responses || [];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((invoice: any) => {
        const invoiceId = invoice.invoiceId?.toLowerCase() || "";
        const requestPaymentDate =
          invoice.requestPaymentDate?.toLowerCase() || "";
        const related = invoice.related?.toLowerCase() || "";
        const invoiceDate = invoice.invoiceDate?.toLowerCase() || "";
        const internalNote = invoice.internalNote?.toLowerCase() || "";
        const total = String(invoice.total || "").toLowerCase();
        const paid = String(invoice.paid || "").toLowerCase();
        const due = String(invoice.due || "").toLowerCase();
        const status = invoice.status?.toLowerCase() || "";

        return (
          invoiceId.includes(query) ||
          requestPaymentDate.includes(query) ||
          related.includes(query) ||
          invoiceDate.includes(query) ||
          internalNote.includes(query) ||
          total.includes(query) ||
          paid.includes(query) ||
          due.includes(query) ||
          status.includes(query)
        );
      });
    }

    if (formData.status) {
      filtered = filtered.filter(
        (invoice: IInvoiceJobDetails) =>
          (invoice.status || "").toLowerCase() === formData.status.toLowerCase()
      );
    }

    return filtered;
  }, [invoiceJobDetailsList?.responses, searchQuery, formData.status]);

  const totalItems = filteredInvoices.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (!showItemsDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowItemsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showItemsDropdown]);

  const handleChangePage = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setPage(1);
    setShowItemsDropdown(false);
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxButtons = 5;

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 3) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", page - 1, page, page + 1, "...", totalPages);
      }
    }

    return pages.filter((p, idx, arr) => !(p === arr[idx - 1]));
  };

  const paginatedInvoices = useMemo(() => {
    return filteredInvoices.slice(
      (page - 1) * itemsPerPage,
      page * itemsPerPage
    );
  }, [filteredInvoices, page, itemsPerPage]);

  const showingCount = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const remaining = Math.max(totalItems - startIndex, 0);
    return Math.min(itemsPerPage, remaining);
  }, [page, itemsPerPage, totalItems]);

  const handleActionClick = (event: React.MouseEvent, jobTask: any) => {
    event.stopPropagation();

    if (activeMenu === jobTask.id) {
      setActiveMenu(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 5,
      left: rect.left - 100,
    });
    setActiveMenu(jobTask.id);
  };

  const handleStatusClick = (
    event: React.MouseEvent,
    invoice: IInvoiceJobDetails
  ) => {
    event.stopPropagation();

    if (statusUpdating[invoice.id]) {
      return;
    }

    if (activeStatusMenu === invoice.id) {
      setActiveStatusMenu(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const dropdownHeight = 300;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const showAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    let left = rect.left;
    const dropdownWidth = 123;
    if (left + dropdownWidth > window.innerWidth) {
      left = window.innerWidth - dropdownWidth - 10;
    }
    if (left < 10) {
      left = 10;
    }

    if (showAbove) {
      const bottom = window.innerHeight - rect.top + 5;
      setStatusMenuPosition({
        top: undefined,
        bottom: bottom,
        left: left,
        showAbove: true,
      });
    } else {
      setStatusMenuPosition({
        top: rect.bottom + 5,
        bottom: undefined,
        left: left,
        showAbove: false,
      });
    }
    setActiveStatusMenu(invoice.id);
  };

  const handleStatusChange = async (invoiceId: string, newStatus: string, currentStatus: string) => {
    setActiveStatusMenu(null);

    const displayStatus = optimisticStatus[invoiceId] || currentStatus;
    if (newStatus === displayStatus || statusUpdating[invoiceId]) {
      return;
    }

    setOptimisticStatus((prev) => ({
      ...prev,
      [invoiceId]: newStatus,
    }));
    setStatusUpdating((prev) => ({
      ...prev,
      [invoiceId]: true,
    }));

    try {
      await jobDetailsApi.updateStatusInvoiceJobDetails(jobId, invoiceId, {
        status: newStatus,
      });

      setOptimisticStatus((prev) => {
        const updated = { ...prev };
        delete updated[invoiceId];
        return updated;
      });
      setStatusUpdating((prev) => {
        const updated = { ...prev };
        delete updated[invoiceId];
        return updated;
      });

      notify("success", "Status updated successfully!");
      dispatch(
        handleGetInvoiceJobDetailsList({
          id: jobId,
          params: {},
        })
      );
    } catch (error: any) {
      setOptimisticStatus((prev) => {
        const updated = { ...prev };
        delete updated[invoiceId];
        return updated;
      });
      setStatusUpdating((prev) => {
        const updated = { ...prev };
        delete updated[invoiceId];
        return updated;
      });
      notify(
        "error",
        `Failed to update status: ${error.response?.data?.message || error?.message || "Unknown error"}`
      );
    }
  };

  const ActionMenu = ({ jobTask, onClose, position }: any) => {
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node)
        ) {
          onClose();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const handleView = () => {
      onClose();
      handleViewDetails(jobTask.id);
    };

    // FB-25: record actions. Email opens View Details with the email modal (it needs the customer).
    const run = (action: () => void) => () => {
      onClose();
      action();
    };
    const openForm = (mode: InvoiceFormSource["mode"]) =>
      run(() => handleCreateInvoice(invoiceJobDetailsList?.responses || [], { mode, invoiceId: jobTask.id }));
    const handleDownload = run(async () => {
      try {
        const response = await jobDetailsApi.downloadInvoicePdf(jobId, jobTask.id);
        downloadBlob(response.data, `Invoice-${jobTask.invoiceUniqueCode || jobTask.id}.pdf`);
      } catch (error: any) {
        notify("error", error?.response?.data?.message || "Failed to download invoice");
      }
    });
    const canEdit = !["Cancelled", "Void"].includes(jobTask.status);

    return (
      <div
        ref={menuRef}
        className={cx("action-menu")}
        style={{
          top: position.top,
          left: position.left,
        }}
      >
        <button className={cx("menu-item")} onClick={handleView}>
          View
        </button>
        {canEdit && (
          <button className={cx("menu-item")} onClick={openForm("edit")}>
            Edit
          </button>
        )}
        <button className={cx("menu-item")} onClick={openForm("duplicate")}>
          Duplicate
        </button>
        <button className={cx("menu-item")} onClick={handleDownload}>
          Download
        </button>
        <button className={cx("menu-item")} onClick={run(() => handleViewDetails(jobTask.id, "email"))}>
          Email
        </button>
        <button className={cx("menu-item")} onClick={run(() => setDeleteInvoiceId(jobTask.id))}>
          Delete
        </button>
      </div>
    );
  };

  const StatusMenu = ({ invoice, onClose, position }: any) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const isUpdating = statusUpdating[invoice.id] || false;

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node)
        ) {
          onClose();
        }
      };

      const handleScroll = () => {
        onClose();
      };

      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", handleScroll, true);
      };
    }, [onClose]);

    if (isUpdating) {
      return null;
    }

    const displayStatus = optimisticStatus[invoice.id] || invoice.status;

    return (
      <div
        ref={menuRef}
        className={cx("status-menu")}
        style={{
          top: position.showAbove ? undefined : position.top,
          bottom: position.showAbove && position.bottom ? position.bottom : undefined,
          left: position.left,
        }}
      >
        {STATUS_OPTIONS.map((status) => (
          <button
            key={status.value}
            className={cx("menu-item", {
              active: displayStatus === status.value,
            })}
            onClick={() => handleStatusChange(invoice.id, status.value, invoice.status)}
          >
            {status.label}
          </button>
        ))}

      </div>
    );
  };

  return (
    <div className={cx("invoice-container")}>
      <div className={cx("invoice-header")}>
        <div className={cx("search-container")}>
          <SearchIcon className={cx("search-icon")} />
          <input
            type="text"
            placeholder="Search invoices"
            className={cx("search-input")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div >
          <PrimarySelectInput
            name="filter"
            placeholder="All Statuses"
            value={formData.status}
            onChange={(value) => handleFieldChange("status", value)}
            options={FILTER_STATUS_OPTIONS}
            className={cx("input")}
            classNameIcon={cx("select-input-icon")}
          />
        </div>

        <PrimaryButton
          title="Create Invoice"
          className={cx("create-button")}
          onClick={() =>
            handleCreateInvoice(invoiceJobDetailsList?.responses || [])
          }
        />
      </div>

      <div className={cx("invoice-table")}>
        {isLoading ? (
          <div className={cx("loading-overlay")}>
            <LoadingMainContent />
          </div>
        ) : (
          <>
            <div className={cx("table-wrapper")}>
              <table className={cx("sections-table")}>
                <thead>
                  <tr>
                    <th className={cx("col-invoice-id")}>Invoice ID</th>
                    <th className={cx("col-request-payment")}>Request Payment</th>
                    <th className={cx("col-related")}>Related</th>
                    <th className={cx("col-invoice-date")}>Invoice Date</th>
                    <th className={cx("col-total")}>Total</th>
                    <th className={cx("col-paid")}>Paid</th>
                    <th className={cx("col-due")}>Due</th>
                    <th className={cx("col-status")}>Status</th>
                    <th className={cx("col-actions")}></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedInvoices.map((invoice: IInvoiceJobDetails) => {
                    const isUpdating = statusUpdating[invoice.id] || false;
                    const displayStatus = optimisticStatus[invoice.id] || invoice.status;
                    const statusClass = displayStatus?.toLowerCase() || "";

                    return (
                      <tr key={invoice.id} className={cx("item-row")}>
                        <td className={cx("col-invoice-id")}>
                          <div className={cx("cell")}>
                            <p className={cx("text-content")}>
                              {invoice.invoiceUniqueCode || ""}
                            </p>
                          </div>
                        </td>
                        <td className={cx("col-request-payment")}>
                          <div className={cx("cell")}>
                            <button
                              className={cx("text-content", "request-payment-text")}
                              onClick={() => setIsRequestPaymentOpen(true)}
                            >
                              Send Text
                            </button>
                          </div>
                        </td>
                        <td className={cx("col-related")}>
                          <div className={cx("cell")}>
                            <p className={cx("text-content")}>{invoice.related || ""}</p>
                          </div>
                        </td>
                        <td className={cx("col-invoice-date")}>
                          <div className={cx("cell")}>
                            <p className={cx("text-content")}>
                              {dayjs(invoice.invoiceDate).format("YYYY-MM-DD")}
                            </p>
                          </div>
                        </td>
                        <td className={cx("col-total")}>
                          <div className={cx("cell")}>
                            <p className={cx("text-content")}>${invoice.total || ""}</p>
                          </div>
                        </td>
                        <td className={cx("col-paid")}>
                          <div className={cx("cell")}>
                            <p className={cx("text-content", "paid-text")}>
                              ${invoice.paid || ""}
                            </p>
                          </div>
                        </td>
                        <td className={cx("col-due")}>
                          <div className={cx("cell")}>
                            <p className={cx("text-content", "due-text")}>
                              ${invoice.due || ""}
                            </p>
                          </div>
                        </td>
                        <td className={cx("col-status")}>
                          <div className={cx("cell")}>
                            <div className={cx("status-button-container")}>
                              <button
                                className={cx("status-button", statusClass, {
                                  updating: isUpdating,
                                })}
                                onClick={(e) => handleStatusClick(e, invoice)}
                                disabled={isUpdating}
                                style={{
                                  opacity: isUpdating ? 0.8 : 1,
                                  cursor: isUpdating ? "not-allowed" : "pointer",
                                }}
                              >
                                {isUpdating ? (
                                  <div className={cx("status-loading")}>
                                    <div className={cx("spinner")}></div>
                                  </div>
                                ) : (
                                  <>
                                    <p className={cx("status-text")}>
                                      {STATUS_OPTIONS.find(
                                        (status) => status.value === displayStatus
                                      )?.label || displayStatus || "-"}
                                    </p>
                                    <ArrowDownIcon className={cx("arrow-down-icon")} />
                                  </>
                                )}
                              </button>
                              {activeStatusMenu === invoice.id && !isUpdating && (
                                <StatusMenu
                                  invoice={invoice}
                                  onClose={() => setActiveStatusMenu(null)}
                                  position={statusMenuPosition}
                                />
                              )}
                            </div>
                          </div>
                        </td>
                        <td className={cx("col-actions")}>
                          <div className={cx("action-icon-container")}>
                            <div onClick={(e) => handleActionClick(e, invoice)}>
                              <ActionIcon className={cx("action-icon")} />
                            </div>
                            {activeMenu === invoice.id && (
                              <ActionMenu
                                jobTask={invoice}
                                onClose={() => setActiveMenu(null)}
                                position={menuPosition}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className={cx("pagination-container")}>
              <div className={cx("pagination-left")}>
                <span className={cx("pagination-text")}>Show result</span>
                <div className={cx("items-per-page-wrapper")} ref={dropdownRef}>
                  <button
                    className={cx("items-per-page-button")}
                    onClick={() => setShowItemsDropdown(!showItemsDropdown)}
                  >
                    <span>{showingCount}</span>
                    <svg
                      width="9"
                      height="9"
                      viewBox="0 0 9 6"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.243 5.656L0 1.414L1.415 0L4.243 2.828L7.071 0L8.486 1.414L4.243 5.656Z"
                        fill="#789EB4"
                      />
                    </svg>
                  </button>
                  {showItemsDropdown && (
                    <div className={cx("items-per-page-dropdown")}>
                      {itemsPerPageOptions.map((option) => (
                        <button
                          key={option}
                          className={cx("dropdown-item", {
                            active: itemsPerPage === option,
                          })}
                          onClick={() => handleItemsPerPageChange(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <span className={cx("pagination-text")}>out of</span>
                <span className={cx("pagination-total")}>{totalItems}</span>
              </div>

              <div className={cx("pagination-right")}>
                <button
                  className={cx("pagination-nav-button", {
                    disabled: page === 1 || page === 0,
                  })}
                  onClick={() => handleChangePage(page - 1)}
                  disabled={page === 1 || page === 0}
                >
                  <ArrowDown className={cx("nav-icon", "rotate-90")} />
                  <span>Previous</span>
                </button>

                <div className={cx("page-numbers")}>
                  {getPageNumbers().map((pageNum, index) => {
                    if (pageNum === "...") {
                      return (
                        <span
                          key={`ellipsis-${index}`}
                          className={cx("page-ellipsis")}
                        >
                          ...
                        </span>
                      );
                    }
                    const pageNumber = Number(pageNum);
                    const isActive = page === pageNumber;
                    return (
                      <button
                        key={pageNumber}
                        className={cx("page-number", {
                          active: isActive,
                        })}
                        onClick={() => handleChangePage(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>

                <button
                  className={cx("pagination-nav-button", {
                    disabled: page === totalPages,
                  })}
                  onClick={() => handleChangePage(page + 1)}
                  disabled={page === totalPages}
                >
                  <span>Next</span>
                  <ArrowDown className={cx("nav-icon", "!rotate-[270deg]")} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      <RequestPaymentModal
        jobId={jobId}
        open={isRequestPaymentOpen}
        onClose={() => setIsRequestPaymentOpen(false)}
        onSuccess={() => setGetListInvoice(true)}
      />
      <ModalAlertDelete
        open={!!deleteInvoiceId}
        description="Are you sure you want to delete this invoice?"
        onCancel={() => setDeleteInvoiceId(null)}
        onConfirm={handleDeleteInvoice}
      />
    </div>
  );
};

export default Invoice;
